import { ReactNode, useLayoutEffect, useRef, useState } from 'react';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  OnChangeFn,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';

/**
 * DataTable — sortable, dense data table.
 *
 * Derived from M3 Data Table (Dense variant) per Agent Protocol step 2:
 *   - Spec:      https://m3.material.io/components/data-table/overview
 *   - Reference: github.com/material-components/material-web (no first-party
 *                data-table at time of authoring; TanStack Table is the
 *                headless engine of choice; M3 spec drives the visual layer)
 *
 * Populus adaptations applied (per Populus/doctrine/design-system.md):
 *   - Compact density: 40px row height (vs M3 default 56px)
 *   - Brand-primary row hover background (`var(--brand-primary-bg)` — follows org)
 *   - Status column accepts any ReactNode (Badge atom typically passed in)
 *   - Optional row click handler (cursor:pointer + on-press feedback)
 *   - Column header uppercase Label-Small per M3 convention
 *
 * Token discipline:
 *   - Row hover background → --brand-primary-bg (brand surface)
 *   - Borders, text, padding → --color-* (system)
 *
 * Phase 11 Block 6 PR 3c (2026-05-12).
 */

export interface DataTableProps<TData> {
  data: TData[];
  columns: ColumnDef<TData>[];
  onRowClick?: (row: TData) => void;
  emptyMessage?: ReactNode;
  /**
   * Controlled sorting (server-side). Pass `sorting` + `onSortingChange` together
   * to lift sort state to the parent — the table then reports header clicks via
   * `onSortingChange` and does NOT re-sort the rows in the browser (manualSorting),
   * so the parent can refetch the page from the server. Omit both to keep the
   * default self-contained client-side sort (every other list page is unaffected).
   */
  sorting?: SortingState;
  onSortingChange?: OnChangeFn<SortingState>;
  /**
   * Optional row-intent prefetch: called with a row's data the first time the
   * pointer enters that row, so a list page can warm the row's DETAIL query
   * before the click. Fires at most once per row (per mount). Absent → no
   * behavior change; the row's hover styling is unaffected either way.
   */
  onRowPrefetch?: (row: TData) => void;
}

// Visually identical to the header text it wraps: the button only adds focus
// and keyboard activation.
const sortButtonStyle: React.CSSProperties = {
  appearance: 'none',
  background: 'none',
  border: 'none',
  padding: 0,
  margin: 0,
  font: 'inherit',
  color: 'inherit',
  textTransform: 'inherit',
  letterSpacing: 'inherit',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

// Anything a keyboard or screen-reader user can land on inside a row.
const CONTROL_SELECTOR = 'a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"]), [role="button"], [role="link"]';

export function DataTable<TData>({
  data,
  columns,
  onRowClick,
  emptyMessage = 'No rows',
  sorting: sortingProp,
  onSortingChange,
  onRowPrefetch,
}: DataTableProps<TData>) {
  // Rows already handed to onRowPrefetch, keyed by the table row id, so a row
  // that's hovered repeatedly only fires the callback once (prefetchQuery would
  // dedupe a fresh entry anyway; this also avoids re-invoking the callback).
  const prefetchedRows = useRef<Set<string>>(new Set());
  const controlled = sortingProp !== undefined && onSortingChange !== undefined;
  const [internalSorting, setInternalSorting] = useState<SortingState>([]);
  const sorting = controlled ? sortingProp : internalSorting;
  // Rows that hold their own controls (a link, a menu button, a checkbox). Such a
  // row stays clickable and keyboard-focusable, but is not announced as a button:
  // a button cannot contain other controls, and a screen reader would flatten
  // them into the row's name. Measured from the rendered cells, so a column that
  // adds a link switches its rows over without the caller saying so.
  const bodyRef = useRef<HTMLTableSectionElement>(null);
  const [rowsWithControls, setRowsWithControls] = useState<ReadonlySet<string>>(new Set());
  useLayoutEffect(() => {
    if (!onRowClick || !bodyRef.current) return;
    const found = new Set<string>();
    bodyRef.current.querySelectorAll<HTMLTableRowElement>('tr[data-row-id]').forEach((tr) => {
      if (tr.querySelector(CONTROL_SELECTOR)) found.add(tr.dataset.rowId!);
    });
    const same = found.size === rowsWithControls.size && [...found].every((id) => rowsWithControls.has(id));
    if (!same) setRowsWithControls(found);
  });

  const table = useReactTable({
    data,
    columns,
    state: { sorting },
    onSortingChange: controlled ? onSortingChange : setInternalSorting,
    manualSorting: controlled,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <div style={{
      background: 'var(--color-card)',
      border: '1px solid var(--color-border)',
      borderRadius: 'var(--r-lg)',
      overflow: 'hidden',
      boxShadow: 'var(--sh-sm)',
    }}>
      {/* Inner scroll container: on narrow viewports a wide table scrolls
          horizontally inside its own card instead of forcing the page wide
          or clipping columns (the outer card keeps the rounded corners).
          `hide-scrollbar` (global.css) suppresses the scrollbar chrome so the
          sideways-scroll affordance is clean on mobile. */}
      <div className="hide-scrollbar" style={{ overflowX: 'auto' }}>
      <table style={{
        width: '100%',
        borderCollapse: 'collapse',
        tableLayout: 'auto',
      }}>
        <thead>
          {table.getHeaderGroups().map(headerGroup => (
            <tr key={headerGroup.id} style={{
              borderBottom: '1px solid var(--color-border)',
              background: 'var(--color-bg)',
            }}>
              {headerGroup.headers.map(header => {
                const isSortable = header.column.getCanSort();
                const sortDir = header.column.getIsSorted();
                const label = flexRender(header.column.columnDef.header, header.getContext());
                return (
                  <th
                    key={header.id}
                    scope="col"
                    aria-sort={isSortable ? (sortDir === 'asc' ? 'ascending' : sortDir === 'desc' ? 'descending' : 'none') : undefined}
                    onClick={isSortable ? header.column.getToggleSortingHandler() : undefined}
                    style={{
                      textAlign: 'left',
                      padding: '10px 16px',
                      fontSize: 'var(--fs-xs)',
                      fontWeight: 'var(--fw-semibold)',
                      textTransform: 'uppercase',
                      letterSpacing: 'var(--ls-wider)',
                      color: 'var(--color-ink-3)',
                      cursor: isSortable ? 'pointer' : 'default',
                      userSelect: 'none',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {isSortable ? (
                      // A sortable header is a real button so it takes keyboard focus
                      // and activates on Enter/Space. It carries no handler of its
                      // own: its click bubbles to the <th>, which keeps the whole
                      // cell clickable exactly as before.
                      <button type="button" style={sortButtonStyle}>
                        {label}
                        <span aria-hidden="true" style={{ marginLeft: 'var(--sp-1)', color: 'var(--color-muted)' }}>
                          {sortDir === 'asc' ? '▲' : sortDir === 'desc' ? '▼' : ''}
                        </span>
                      </button>
                    ) : label}
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody ref={bodyRef}>
          {table.getRowModel().rows.length === 0 ? (
            <tr>
              <td colSpan={columns.length} style={{
                padding: 'var(--sp-7) var(--sp-4)',
                textAlign: 'center',
                color: 'var(--color-muted)',
                fontSize: 'var(--fs-md)',
              }}>
                {emptyMessage}
              </td>
            </tr>
          ) : (
            table.getRowModel().rows.map(row => (
              <tr
                key={row.id}
                data-row-id={row.id}
                onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                // Accessibility + mobile/touch reliability (report-row-clickable):
                // a clickable row is a real button target — role='button' + tabIndex
                // make it focusable and announced, and Enter/Space activate it. This
                // is what makes the tap land on iOS Safari (a focusable element
                // reliably fires click) and lets keyboard users open records. A row
                // holding its own controls keeps the tabIndex and keys but not the
                // role (see rowsWithControls above).
                role={onRowClick && !rowsWithControls.has(row.id) ? 'button' : undefined}
                tabIndex={onRowClick ? 0 : undefined}
                onKeyDown={onRowClick ? (e) => {
                  // Only a key pressed ON the row opens it; Enter on a link or
                  // button inside the row belongs to that control.
                  if (e.target !== e.currentTarget) return;
                  if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
                    // Space would otherwise scroll the page; keep activation crisp.
                    e.preventDefault();
                    onRowClick(row.original);
                  }
                } : undefined}
                style={{
                  borderBottom: '1px solid var(--color-border)',
                  height: 40,  // compact density per doctrine
                  cursor: onRowClick ? 'pointer' : 'default',
                  transition: 'background 120ms ease',
                }}
                onMouseEnter={(e) => {
                  if (onRowClick) e.currentTarget.style.background = 'var(--brand-primary-bg)';
                  if (onRowPrefetch && !prefetchedRows.current.has(row.id)) {
                    prefetchedRows.current.add(row.id);
                    onRowPrefetch(row.original);
                  }
                }}
                onMouseLeave={(e) => {
                  if (onRowClick) e.currentTarget.style.background = '';
                }}
                onFocus={(e) => {
                  // Keyboard focus mirrors hover so the active row is visible.
                  if (onRowClick) e.currentTarget.style.background = 'var(--brand-primary-bg)';
                }}
                onBlur={(e) => {
                  if (onRowClick) e.currentTarget.style.background = '';
                }}
              >
                {row.getVisibleCells().map(cell => (
                  <td key={cell.id} style={{
                    padding: '8px 16px',
                    fontSize: 'var(--fs-md)',
                    color: 'var(--color-ink)',
                    verticalAlign: 'middle',
                    // Keep single-value cells (dates like 'Jan 31, 2027', money,
                    // statuses, ids) on one line — at phone width they otherwise
                    // wrap onto 2-3 lines and crush the column. The table as a
                    // whole scrolls sideways inside its card (overflowX above),
                    // so nowrap never forces the PAGE to scroll horizontally.
                    // Cells needing wrap can override via a custom cell renderer.
                    whiteSpace: 'nowrap',
                  }}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
      </div>
    </div>
  );
}
